import { BodyRegular, FlexColMd, FlexColSm, Headline3 } from 'components/Elements';
import NavBackButton from 'components/buttons/NavBackButton';
import { GenerateTypeContentComponent } from '../GenerateTypeContentComponent';
import useModifyRequest from 'hooks/useModifyRequest';
import React from 'react';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import strings from '../../../../../strings/strings.json';
import Hint from 'components/Hint';
import Alert, { AlertError } from 'components/alert/Alert';
import useDeleteRequest from 'hooks/useDeleteRequest';
import useWindowWidth from 'hooks/useWindowWidth';
import {
    ButtonsRow,
    Container,
    ContentCard,
    EditAndApproveButtonsContainer,
    ErrorRow,
    ReviewGeneratedContentProps,
} from './ReviewGeneratedSharedComponents';
import AudioPlayer from 'pages/learner/components/AudioPlayer';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { AudioDocument, FileDocumentType } from 'models/FileDocument';
import Button from 'components/buttons/Button';

export default function ReviewGeneratedAudioComponent({ item, backButton }: ReviewGeneratedContentProps): JSX.Element {
    const context = React.useContext(BuildCourseContext);
    const [showError, setShowError] = React.useState<boolean>();
    const [openRejectAlert, setOpenRejectAlert] = React.useState(false);
    const [alertError, setAlertError] = React.useState<AlertError>();

    const { modifyData: approveAudio, loading } = useModifyRequest(
        `generate-content-statuses/${item.id}/approve`,
        'PUT',
    );
    const { deleteData: rejectStatus, deleteInProgress: rejecting } = useDeleteRequest(
        `generate-content-statuses/${item.id}`,
    );

    const { isDesktopWidth } = useWindowWidth();

    const approveClick = async () => {
        const { value, errors } = await approveAudio({});

        if ((errors && errors.length > 0) || (!value as boolean)) {
            setShowError(true);
        } else {
            if (context.editMode) {
                const audioDocument: AudioDocument = {
                    id: '',
                    filename: '',
                    s3Filepath: item.s3Filepath,
                    dateUpdated: new Date(),
                    documentType: FileDocumentType.AUDIO,
                };
                context.setCourseState((prev) => {
                    if (!prev) return prev;
                    return {
                        ...prev,
                        modules: prev.modules.map((module) => ({
                            ...module,
                            activities: module.activities.map((activity) =>
                                activity.id === item.activityId
                                    ? { ...activity, audioDocument: audioDocument }
                                    : activity,
                            ),
                        })),
                    };
                });
            } else {
                await context.refetchCourse();
            }

            backButton();
        }
    };

    const rejectClick = async () => {
        const { value, errors } = await rejectStatus();

        if ((errors && errors.length > 0) || (value as boolean) === false) {
            const error: AlertError = {
                title: strings.courseAIGeneratedContentComponent.rejectAlert.alertErrorTitle,
                description: strings.courseAIGeneratedContentComponent.rejectAlert.alertErrorDescription,
            };
            setAlertError(error);
        } else {
            await context.refetchGenerateContentStatuses();
            backButton();
        }
    };

    return (
        <>
            {openRejectAlert && (
                <Alert
                    buttonText={strings.courseAIGeneratedContentComponent.rejectAlert.alertButton}
                    ariaButtonText={strings.courseAIGeneratedContentComponent.rejectAlert.alertButtonAria}
                    alertText={strings.courseAIGeneratedContentComponent.rejectAlert.alertText}
                    closeAlert={() => setOpenRejectAlert(false)}
                    onSubmit={rejectClick}
                    submitting={rejecting}
                    error={alertError}
                />
            )}
            <Container>
                <NavBackButton
                    backButtonTitle={''}
                    backButtonAria={strings.courseAIGeneratedContentComponent.backButtonAria}
                    backButtonHref={''}
                    buttonFunctionOverride={backButton}
                />

                <ContentCard>
                    <Headline3>{`${strings.courseAIGeneratedContentComponent.activityPrefix} ${item.activityOrder}: ${item.activityTitle}`}</Headline3>
                    <Hint hint={strings.courseAIGeneratedContentComponent.textToAudioHint} />
                    <FlexColMd>
                        <FlexColSm>
                            <GenerateTypeContentComponent generateType={item.generateType} />
                            <AudioPlayer filepath={item.s3Filepath} />
                        </FlexColSm>
                        <BodyRegular>{item.textContent}</BodyRegular>
                    </FlexColMd>
                    <ButtonsRow>
                        <Button
                            title={strings.courseAIGeneratedContentComponent.rejectButton.title}
                            aria={strings.courseAIGeneratedContentComponent.rejectButton.aria}
                            onClick={() => setOpenRejectAlert(true)}
                            colour={'BLACK'}
                        />
                        <EditAndApproveButtonsContainer>
                            {isDesktopWidth && (
                                <Button
                                    title={strings.courseAIGeneratedContentComponent.editTextButton.title}
                                    aria={strings.courseAIGeneratedContentComponent.editTextButton.aria}
                                    onClick={() => {
                                        context.setActiveStep('courseStructureAndContent');
                                        context.setEditActivityId(item.activityId);
                                    }}
                                />
                            )}
                            <Button
                                title={strings.courseAIGeneratedContentComponent.approveAudioButton.title}
                                aria={strings.courseAIGeneratedContentComponent.approveAudioButton.aria}
                                onClick={approveClick}
                                submitting={loading}
                                colour="PRIMARY"
                            />
                        </EditAndApproveButtonsContainer>
                    </ButtonsRow>
                    {showError && (
                        <ErrorRow>
                            <ErrorNotifierSlim description={strings.courseAIGeneratedContentComponent.approveError} />
                        </ErrorRow>
                    )}
                </ContentCard>
            </Container>
        </>
    );
}
